import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumOverlay } from '../../components/ui/premium-overlay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import {
  ArrowLeft,
  Upload,
  UserCircle,
  X,
  CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BD_PHONE_REGEX = /^01[3-9]\d{8}$/;

const ROLES = [
  { value: 'mechanic', en: 'Mechanic', bn: 'মেকানিক' },
  { value: 'helper', en: 'Helper', bn: 'হেল্পার' },
  { value: 'manager', en: 'Manager', bn: 'ম্যানেজার' },
  { value: 'electrician', en: 'Electrician', bn: 'ইলেকট্রিশিয়ান' },
  { value: 'painter', en: 'Painter', bn: 'পেইন্টার' },
  { value: 'cleaner', en: 'Cleaner', bn: 'ক্লিনার' },
  { value: 'accountant', en: 'Accountant', bn: 'হিসাবরক্ষক' },
  { value: 'other', en: 'Other', bn: 'অন্যান্য' },
];

export default function EmployeeCreatePage() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isPremium } = useAuth();
  const { toast } = useToast();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  const fileInputRef = useRef(null);
  const nidFrontRef = useRef(null);
  const nidBackRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    role: 'mechanic',
    nid: '',
    nid_front: null,
    nid_back: null,
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    monthly_salary: '',
    join_date: new Date().toISOString().slice(0, 10),
    photo: null,
    notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: language === 'en' ? 'Photo too large (max 5MB)' : 'ছবি অনেক বড় (সর্বোচ্চ ৫MB)', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result);
      handleChange('photo', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    handleChange('photo', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleNidPhoto = (field, ref) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: language === 'en' ? 'Photo too large (max 5MB)' : 'ছবি অনেক বড় (সর্বোচ্চ ৫MB)', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => handleChange(field, reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast({ title: language === 'en' ? 'Name and phone are required' : 'নাম ও ফোন আবশ্যক', variant: 'destructive' });
      return;
    }
    if (!BD_PHONE_REGEX.test(formData.phone.trim())) {
      toast({ title: language === 'en' ? 'Invalid BD phone number (01XXXXXXXXX)' : 'ভুল ফোন নম্বর (01XXXXXXXXX)', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        monthly_salary: parseFloat(formData.monthly_salary) || 0,
      };
      const res = await axios.post(`${API}/employees`, payload);
      toast({ title: language === 'en' ? 'Employee added!' : 'কর্মচারী যুক্ত হয়েছে!' });
      navigate(`${basePath}/employees/${res.data.id}`);
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <PremiumOverlay feature={language === 'en' ? 'Employee & Payroll Management' : 'কর্মচারী ও বেতন ব্যবস্থাপনা'} locked={!isPremium}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(`${basePath}/employees`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold font-['Barlow_Condensed']">
            {language === 'en' ? 'Add Employee' : 'কর্মচারী যোগ করুন'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Photo */}
          <Card className="mb-6">
            <CardHeader><CardTitle>{language === 'en' ? 'Photo' : 'ছবি'}</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {photoPreview ? (
                  <div className="relative">
                    <img src={photoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover" />
                    <button type="button" onClick={removePhoto} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
                    <UserCircle className="w-10 h-10 text-slate-300" />
                  </div>
                )}
                <div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Upload Photo' : 'ছবি আপলোড'}
                  </Button>
                  <p className="text-xs text-slate-400 mt-1">{language === 'en' ? 'Will be compressed automatically' : 'স্বয়ংক্রিয়ভাবে সংকুচিত হবে'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader><CardTitle>{language === 'en' ? 'Basic Information' : 'মৌলিক তথ্য'}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Full Name' : 'পুরো নাম'} <span className="text-red-500">*</span></Label>
                  <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder={language === 'en' ? 'e.g. Rahim Mia' : 'যেমন: রহিম মিয়া'} />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Phone' : 'ফোন'} <span className="text-red-500">*</span></Label>
                  <Input value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="01XXXXXXXXX" maxLength={11} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Role / Designation' : 'পদবী'}</Label>
                  <Select value={formData.role} onValueChange={v => handleChange('role', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value}>{language === 'en' ? r.en : r.bn}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'en' ? 'NID Number' : 'NID নম্বর'}</Label>
                  <Input value={formData.nid} onChange={e => handleChange('nid', e.target.value)} placeholder={language === 'en' ? 'National ID' : 'জাতীয় পরিচয়পত্র'} />
                </div>
              </div>

              {/* NID Photos */}
              <div>
                <Label className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4" />{language === 'en' ? 'NID Photos' : 'NID ছবি'}</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center">
                    {formData.nid_front ? (
                      <div className="relative">
                        <img src={formData.nid_front} alt="NID Front" className="w-full h-28 object-contain rounded" />
                        <button type="button" onClick={() => { handleChange('nid_front', null); if (nidFrontRef.current) nidFrontRef.current.value = ''; }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div>
                        <input ref={nidFrontRef} type="file" accept="image/*" className="hidden" onChange={handleNidPhoto('nid_front', nidFrontRef)} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => nidFrontRef.current?.click()} className="text-slate-400">
                          <Upload className="w-4 h-4 mr-1" />{language === 'en' ? 'Front Side' : 'সামনের দিক'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center">
                    {formData.nid_back ? (
                      <div className="relative">
                        <img src={formData.nid_back} alt="NID Back" className="w-full h-28 object-contain rounded" />
                        <button type="button" onClick={() => { handleChange('nid_back', null); if (nidBackRef.current) nidBackRef.current.value = ''; }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div>
                        <input ref={nidBackRef} type="file" accept="image/*" className="hidden" onChange={handleNidPhoto('nid_back', nidBackRef)} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => nidBackRef.current?.click()} className="text-slate-400">
                          <Upload className="w-4 h-4 mr-1" />{language === 'en' ? 'Back Side' : 'পিছনের দিক'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>{language === 'en' ? 'Address' : 'ঠিকানা'}</Label>
                <Input value={formData.address} onChange={e => handleChange('address', e.target.value)} />
              </div>
              <div>
                <Label>{language === 'en' ? 'Join Date' : 'যোগদানের তারিখ'}</Label>
                <Input type="date" value={formData.join_date} onChange={e => handleChange('join_date', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Salary & Emergency */}
          <Card className="mb-6">
            <CardHeader><CardTitle>{language === 'en' ? 'Salary & Emergency Contact' : 'বেতন ও জরুরি যোগাযোগ'}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{language === 'en' ? 'Monthly Salary (৳)' : 'মাসিক বেতন (৳)'}</Label>
                <Input type="number" min="0" value={formData.monthly_salary} onChange={e => handleChange('monthly_salary', e.target.value)} placeholder="0" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Emergency Contact Name' : 'জরুরি যোগাযোগের নাম'}</Label>
                  <Input value={formData.emergency_contact} onChange={e => handleChange('emergency_contact', e.target.value)} />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Emergency Phone' : 'জরুরি ফোন'}</Label>
                  <Input value={formData.emergency_phone} onChange={e => handleChange('emergency_phone', e.target.value)} placeholder="01XXXXXXXXX" />
                </div>
              </div>
              <div>
                <Label>{language === 'en' ? 'Notes' : 'নোট'}</Label>
                <Input value={formData.notes} onChange={e => handleChange('notes', e.target.value)} placeholder={language === 'en' ? 'Any additional notes...' : 'অতিরিক্ত নোট...'} />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(`${basePath}/employees`)}>
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </Button>
            <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={loading}>
              {loading && <Spinner size="sm" className="mr-2" />}
              {language === 'en' ? 'Save Employee' : 'কর্মচারী সেভ করুন'}
            </Button>
          </div>
        </form>
      </div>
      </PremiumOverlay>
    </AdminLayout>
  );
}
